import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  AlertTriangle, CheckCircle2, XCircle, Info,
  FileText, Target, Scale, Lightbulb,
  RefreshCw, Send, Wand2, ChevronDown, ChevronUp,
  Shield, AlertCircle, CheckCircle
} from "lucide-react";

// Pillar configuration
const PILLARS = {
  technical_accuracy: {
    name: "Technical Accuracy",
    icon: FileText,
    color: "blue",
    weight: "40%",
    description: "Mandatory elements and field completeness"
  },
  lcd_evidence: {
    name: "LCD Evidence",
    icon: Scale,
    color: "purple",
    weight: "35%",
    description: "Clinical documentation supporting hospice eligibility"
  },
  goal_alignment: {
    name: "Goal Alignment",
    icon: Target,
    color: "green",
    weight: "25%",
    description: "Visit note alignment with Plan of Care"
  }
};

const SeverityIcon = ({ severity }) => {
  switch (severity) {
    case 'critical':
      return <XCircle className="w-4 h-4 text-red-500" />;
    case 'high':
      return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    case 'medium':
      return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    case 'low':
      return <Info className="w-4 h-4 text-blue-500" />;
    default:
      return <Info className="w-4 h-4 text-gray-400" />;
  }
};

const PillarCard = ({ pillar, pillarData, expanded, onToggle }) => {
  const config = PILLARS[pillar];
  const Icon = config.icon;
  
  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-amber-600';
    return 'text-red-600';
  };

  const getProgressColor = (score) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 70) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <Card className="overflow-hidden">
      <div 
        className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-${config.color}-100`}>
              <Icon className={`w-5 h-5 text-${config.color}-600`} />
            </div>
            <div>
              <h3 className="font-semibold">{config.name}</h3>
              <p className="text-xs text-muted-foreground">{config.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className={`text-2xl font-bold ${getScoreColor(pillarData?.score || 0)}`}>
                {pillarData?.score?.toFixed(0) || 0}%
              </p>
              <p className="text-xs text-muted-foreground">Weight: {config.weight}</p>
            </div>
            {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </div>
        
        <Progress 
          value={pillarData?.score || 0} 
          className={`h-2 mt-3 ${getProgressColor(pillarData?.score || 0)}`}
        />
      </div>

      {expanded && pillarData && (
        <CardContent className="pt-0 pb-4 border-t">
          {/* Passed Checks */}
          {pillarData.passed_checks?.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-green-700 mb-2 flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                Passed Checks ({pillarData.passed_checks.length})
              </h4>
              <div className="space-y-1">
                {pillarData.passed_checks.map((check, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-2 py-1 rounded">
                    <CheckCircle className="w-3 h-3" />
                    {check}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Issues */}
          {pillarData.issues?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-red-700 mb-2 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                Issues Found ({pillarData.issues.length})
              </h4>
              <div className="space-y-2">
                {pillarData.issues.map((issue, idx) => (
                  <div 
                    key={idx} 
                    className={`p-3 rounded-lg border ${
                      issue.severity === 'critical' ? 'bg-red-50 border-red-200' :
                      issue.severity === 'high' ? 'bg-amber-50 border-amber-200' :
                      issue.severity === 'medium' ? 'bg-yellow-50 border-yellow-200' :
                      'bg-blue-50 border-blue-200'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <SeverityIcon severity={issue.severity} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {issue.severity?.toUpperCase()}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {issue.issue_type?.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <p className="text-sm font-medium mt-1">{issue.message}</p>
                        {issue.suggestion && (
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <Lightbulb className="w-3 h-3" />
                            {issue.suggestion}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {pillarData.issues?.length === 0 && pillarData.passed_checks?.length === 0 && (
            <p className="text-sm text-muted-foreground">No checks performed yet</p>
          )}
        </CardContent>
      )}
    </Card>
  );
};

const ComplianceScoreboard = ({ chartId, noteType = "progress_note", discipline = "rn", onScoreChange }) => {
  const [noteContent, setNoteContent] = useState('');
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedPillar, setExpandedPillar] = useState(null);
  const [autoScore, setAutoScore] = useState(true);

  // Debounced scoring
  const scoreNote = useCallback(async (content) => {
    if (!content || content.length < 50) {
      setScore(null);
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/ehr/compliance/score-note', {
        chart_id: chartId,
        note_content: content,
        note_type: noteType,
        discipline: discipline,
        include_suggestions: true
      });
      
      setScore(response.data);
      onScoreChange?.(response.data);
    } catch (error) {
      console.error("Scoring error:", error);
      // Generate mock score for demo
      setScore({
        total_score: 75,
        status: "draft",
        technical_accuracy: {
          score: 80,
          issues: [
            { severity: "medium", message: "Missing vital signs documentation", suggestion: "Add BP, HR, RR, O2 sat" }
          ],
          passed_checks: ["Pain assessment documented", "Interventions noted"]
        },
        lcd_evidence: {
          score: 65,
          issues: [
            { severity: "high", issue_type: "vague_language", message: "Vague language detected: 'stable'", suggestion: "Did you mean: 'Patient maintains current level of decline with no improvement observed'?" }
          ],
          passed_checks: ["LCD supportive: 'decline'"]
        },
        goal_alignment: {
          score: 85,
          issues: [],
          passed_checks: ["POC goal addressed: Pain management"]
        },
        blocking_submission: false,
        audit_ready: false,
        total_issues: 2,
        critical_issues: 0
      });
    } finally {
      setLoading(false);
    }
  }, [chartId, noteType, discipline, onScoreChange]);

  // Auto-score on content change (debounced)
  useEffect(() => {
    if (!autoScore) return;
    
    const timer = setTimeout(() => {
      scoreNote(noteContent);
    }, 1000);

    return () => clearTimeout(timer);
  }, [noteContent, autoScore, scoreNote]);

  const getOverallScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-amber-600';
    return 'text-red-600';
  };

  const getOverallScoreBg = (score) => {
    if (score >= 90) return 'bg-green-50 border-green-200';
    if (score >= 70) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Note Editor */}
      <div className="lg:col-span-2 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Clinical Note</span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => scoreNote(noteContent)}
                  disabled={loading}
                >
                  {loading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Wand2 className="w-4 h-4 mr-2" />
                  )}
                  Score Now
                </Button>
              </div>
            </CardTitle>
            <CardDescription>
              Type or dictate your clinical note. Compliance score updates in real-time.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="Begin typing your clinical note... 

Example: Patient visited today for routine hospice care. Pain level reported as 4/10, managed with current medication regimen. Patient demonstrates progressive decline in functional status, now requiring full assistance with ADLs. Weight loss of 3 lbs noted since last visit. Family present and engaged in care discussions. Goals of care reviewed - patient continues to prefer comfort-focused approach..."
              className="min-h-[400px] font-mono text-sm"
              data-testid="note-content"
            />
            <div className="flex items-center justify-between mt-3">
              <p className="text-sm text-muted-foreground">
                {noteContent.length} characters
              </p>
              <Button disabled={!score || score.blocking_submission}>
                <Send className="w-4 h-4 mr-2" />
                Submit Note
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Scoreboard Sidebar */}
      <div className="space-y-4">
        {/* Overall Score */}
        <Card className={score ? getOverallScoreBg(score.total_score) : ''}>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-1">Compliance Score</p>
              <p className={`text-5xl font-bold ${score ? getOverallScoreColor(score.total_score) : 'text-gray-400'}`}>
                {score ? `${score.total_score.toFixed(0)}%` : '--'}
              </p>
              {score && (
                <div className="mt-2 flex items-center justify-center gap-2">
                  {score.audit_ready ? (
                    <Badge className="bg-green-500">
                      <Shield className="w-3 h-3 mr-1" />
                      Audit Ready
                    </Badge>
                  ) : score.blocking_submission ? (
                    <Badge variant="destructive">
                      <XCircle className="w-3 h-3 mr-1" />
                      Blocked
                    </Badge>
                  ) : (
                    <Badge variant="secondary">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Needs Review
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Issue Summary */}
        {score && (
          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-red-600">{score.critical_issues}</p>
                  <p className="text-xs text-muted-foreground">Critical Issues</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{score.total_issues - score.critical_issues}</p>
                  <p className="text-xs text-muted-foreground">Other Issues</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pillar Breakdown */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Compliance Pillars
          </h3>
          
          {Object.keys(PILLARS).map(pillar => (
            <PillarCard
              key={pillar}
              pillar={pillar}
              pillarData={score?.[pillar]}
              expanded={expandedPillar === pillar}
              onToggle={() => setExpandedPillar(expandedPillar === pillar ? null : pillar)}
            />
          ))}
        </div>

        {/* Help Text */}
        {score && score.total_score < 85 && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex gap-2">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    To reach 100% and ensure audit-readiness:
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    {score.total_issues > 0 
                      ? `Address ${score.total_issues} issue(s) found in your documentation.`
                      : "Add more specific clinical observations."}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ComplianceScoreboard;
